import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { expandLegacyOperatorPermissions } from './utils/expandLegacyOperatorPermissions';
import { getRememberedLoginRoute } from './utils/appOperatorSession';
import { isLocalInternalHost, isLocalInternalPath } from './utils/localInternalHost';
import { getFranchiseeOperatorAllowedPaths } from './config/franchiseeOperatorPermissions';
import {
  generateAdminControlPermissionName,
} from './config/adminControlScreens';

// Layout Components
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';
import UserLayout from './layouts/UserLayout';

// Auth Pages
import Login from './pages/Auth/Login';
import UserLogin from './pages/Auth/UserLogin';
import MagicLogin from './pages/Auth/MagicLogin';
import PortalHome from './pages/Home/PortalHome';
import LoksyLanding from './pages/Home/LoksyLanding';
import LoksyMitraStory from './pages/Home/LoksyMitraStory';
import LoksyAppStory from './pages/Home/LoksyAppStory';
import LoksyDemo from './pages/Home/LoksyDemo';
import NotFound from './pages/NotFound/NotFound';
import PublicShopCatalog from './pages/Public/PublicShopCatalog';
import PublicTokenBoard from './pages/Public/PublicTokenBoard';
import CitizenHome from './pages/Public/CitizenHome';
import CitizenHomeComplete from './pages/Public/CitizenHomeComplete';
import CitizenMyTown from './pages/Public/CitizenMyTown';
import PublicEnquiryForm from './pages/Public/PublicEnquiryForm';

// Dashboard Pages
import Dashboard from './pages/Dashboard/Dashboard';
import UserManagement from './pages/Users/UserManagement';
import UserDetails from './pages/Users/UserDetails';
import UserAnalytics from './pages/Users/UserAnalytics';
import ViewBills from './pages/Bills/ViewBills';
import OldSiteBills from './pages/Bills/OldSiteBills';
import PendingWorks from './pages/Bills/PendingWorks';
import SmartBillDrafts from './pages/Bills/SmartBillDrafts';
import Enquiries from './pages/Bills/Enquiries';
import EnquiryForm from './pages/Bills/EnquiryForm';
import PostBillCoupons from './pages/Bills/PostBillCoupons';
import LeadsManagementTabs from './pages/Bills/LeadsManagementTabs';
import CustomerDashboard from './pages/Customer/CustomerDashboard';
import CustomerSearch from './pages/Customer/CustomerSearch';
import ServiceChargesChallans from './pages/Bills/ServiceChargesChallans';
import CounterSalesDatewise from './pages/Bills/CounterSalesDatewise';
import ServicesManagement from './pages/Bills/ServicesManagement';
import MainCategoriesManagement from './pages/Bills/MainCategoriesManagement';
import InventoryHub from './pages/Bills/InventoryHub';
import WhatsAppMessageConfig from './pages/Bills/WhatsAppMessageConfig';
import AdminControls from './pages/Bills/AdminControls';
import BillTaxesSettings from './pages/Bills/BillTaxesSettings';
import KNLSServices from './pages/Services/KNLSServices';
import TrainingPrograms from './pages/TrainingPrograms/TrainingPrograms';
import TrainingProgramsManagement from './pages/TrainingPrograms/TrainingProgramsManagement';
import CouponsPage from './pages/Coupons/CouponsPage';
import ProfessionsManagement from './pages/Professions/ProfessionsManagement';
import JAlertsMngmt from './pages/JobAlerts/jalertsmngmt';
import JobAlertsView from './pages/JobAlerts/JobAlertsView';
import JobAlertsApproval from './pages/JobAlerts/JobAlertsApproval';
import SubscribersManagement from './pages/JobAlerts/SubscribersManagement';
import PublicJobAlerts from './pages/JobAlerts/PublicJobAlerts';
import JobAlertsEnquiries from './pages/JobAlerts/JobAlertsEnquiries';
import ShopsManagement from './pages/Shops/ShopsManagement';
import NetCentersManagement from './pages/NetCenters/NetCentersManagement';
import OnlineWorksManagement from './pages/OnlineWorks/OnlineWorksManagement';
import Settings from './pages/Settings/Settings';
import Profile from './pages/Profile/Profile';
import SessionManagement from './pages/Profile/SessionManagement';
import Backup from './pages/Backup/Backup';
import DashboardCards from './pages/DashboardCards/DashboardCards';
import CatalogTemplates from './pages/CatalogTemplates/CatalogTemplates';
import MitraScreens from './pages/MitraScreens/MitraScreens';
import CitizenScreens from './pages/CitizenScreens/CitizenScreens';
import MyTownScreens from './pages/MyTownScreens/MyTownScreens';
import TestersFeedback from './pages/TestersFeedback/TestersFeedback';
import BetaGate from './pages/BetaGate/BetaGate';
import CouponFlow from './pages/AppAdmin/CouponFlow';
import CouponManagementAdmin from './pages/AppAdmin/CouponManagementAdmin';
import BoostControl from './pages/AppAdmin/BoostControl';
import CoinManagement from './pages/AppAdmin/CoinManagement';
import RazorpayLedger from './pages/AppAdmin/RazorpayLedger';
import ShopPollsAdmin from './pages/AppAdmin/ShopPollsAdmin';
import AppTrends from './pages/AppAdmin/AppTrends';
import LuckyWheel from './pages/AppAdmin/LuckyWheel';
import MyTables from './pages/Tables/MyTables';
import OldSite from './pages/OldSite/OldSite';
import StudentRegistration from './pages/Students/StudentRegistration';
import StudentRegistrationWithBill from './pages/Students/StudentRegistrationWithBill';
import StudentDashboard from './pages/Students/StudentDashboard';
import StudentVerification from './pages/Students/StudentVerification';
import StudentLogin from './pages/Students/StudentLogin';
import StudentDashboardPage from './pages/Students/StudentDashboardPage';
import StudentReferralsPage from './pages/Students/StudentReferralsPage';
import DailyTrackSheet from './pages/Students/DailyTrackSheet';
import TrackSheetHistory from './pages/Students/TrackSheetHistory';
import StudentStatistics from './pages/Students/StudentStatistics';
import CertificateVerification from './pages/Students/CertificateVerification';
import StudentTrackSheetsAdmin from './pages/Students/StudentTrackSheetsAdmin';
import AdminTrackSheetsDashboard from './pages/Students/AdminTrackSheetsDashboard';
import AdminAttendance from './pages/Students/AdminAttendance';
import CourseManagement from './pages/Students/CourseManagement';
import LearnZonePeerReferrals from './pages/Students/LearnZonePeerReferrals';
import CourseDisplaySettingsPage from './pages/Students/CourseDisplaySettingsPage';
import AssignmentReviewPage from './pages/Students/AssignmentReviewPage';
import QuizReviewPage from './pages/Students/QuizReviewPage';
import StudentReportsPage from './pages/Students/StudentReportsPage';
import CodingReviewPage from './pages/Students/CodingReviewPage';
import CodingProblemsPage from './pages/Students/CodingProblemsPage';
import FlashcardsPage from './pages/Students/FlashcardsPage';
import MyCourses from './pages/Students/MyCourses';
import StudentCourseViewer from './pages/Students/StudentCourseViewer';
import CodingChallengeScreen from './pages/Students/CodingChallengeScreen';
import PracticeIdeScreen from './pages/Students/PracticeIdeScreen';
import TypingTutorHome from './pages/Students/TypingTutor/TypingTutorHome';
import TypingTutorExerciseList from './pages/Students/TypingTutor/TypingTutorExerciseList';
import TypingTutorLesson from './pages/Students/TypingTutor/TypingTutorLesson';
import TypingTutorGamesHub from './pages/Students/TypingTutor/TypingTutorGamesHub';
import TypingTutorGamePlay from './pages/Students/TypingTutor/TypingTutorGamePlay';
import LmsLeaderboardPage from './pages/Students/LmsLeaderboardPage';
import StudentLiveCertificatePage from './pages/Students/StudentLiveCertificatePage';
import ApplicationChat from './pages/Confirmations/ApplicationChat';
import URLManager from './pages/URLManager/URLManagerResponsive';
import LD2HDUpscaler from './pages/LD2HD/LD2HDUpscaler';
import AppNotifications from './pages/AppNotifications/AppNotifications';

// User Dashboard Pages
import UserDashboard from './pages/UserDashboard/UserDashboard';
import UserCouponsView from './pages/UserDashboard/UserCouponsView';
import UserShopOrders from './pages/UserDashboard/UserShopOrders';
import RedeemCouponOrder from './pages/UserDashboard/RedeemCouponOrder';
import ShopOrdersInbox from './pages/ShopOrders/ShopOrdersInbox';
import UserJobAlertsView from './pages/UserDashboard/UserJobAlertsView';
import UserAdsView from './pages/UserDashboard/UserAdsView';
import CareerProfileForm from './pages/UserDashboard/CareerProfileForm';
import ResumeGeneration from './pages/UserDashboard/ResumeGeneration';
import JobAlertsRegistration from './pages/UserDashboard/JobAlertsRegistration';
import Disclaimer from './pages/Disclaimer/Disclaimer';
import TermsPage from './pages/TermsPage';
import LocsyTermsPage from './pages/LocsyTermsPage';

// Franchisee Dashboard Pages
import FranchiseeDashboard from './pages/FranchiseeDashboard/FranchiseeDashboard';
import ShopPhotosUpdate from './pages/FranchiseeDashboard/ShopPhotosUpdate';
import ProductsHub from './pages/FranchiseeDashboard/ProductsHub';
import AccountsHub from './pages/FranchiseeDashboard/AccountsHub';
import FranchiseeAdminRegister from './pages/Franchisee/FranchiseeAdminRegister';
import FranchiseeAdminLogin from './pages/Franchisee/FranchiseeAdminLogin';
import FranchiseeOperatorLogin from './pages/Franchisee/FranchiseeOperatorLogin';
import FranchiseeAdminApproval from './pages/Franchisee/FranchiseeAdminApproval';
import AssistedRegistrationPage from './pages/Franchisee/AssistedRegistrationPage';
import FranchiseeSaasPricingAdmin from './pages/Franchisee/FranchiseeSaasPricingAdmin';
import RazorpayCheckoutDemo from './pages/Dev/RazorpayCheckoutDemo';
import OperatorManagement from './pages/Franchisee/OperatorManagement';
import ForgotPassword from './pages/Franchisee/ForgotPassword';
import ForgotPin from './pages/Franchisee/ForgotPin';
import MyOperator from './pages/AppAdmin/MyOperator';
import BusinessCategoryIndex from './pages/AppAdmin/BusinessCategoryIndex';
import MyTownBusinessListings from './pages/AppAdmin/MyTownBusinessListings';
import VyaparFailedOtp from './pages/AppAdmin/VyaparFailedOtp';
import BusinessTickSettings from './pages/AppAdmin/BusinessTickSettings';
import MitraTokenBoardSettings from './pages/AppAdmin/MitraTokenBoardSettings';
import CitizenPostingCategories from './pages/AppAdmin/CitizenPostingCategories';
import UserPostings from './pages/AppAdmin/UserPostings';
import AdvertisementSettings from './pages/AppAdmin/AdvertisementSettings';
import CareerResumeSettings from './pages/AppAdmin/CareerResumeSettings';
import LearnZoneApprovals from './pages/AppAdmin/LearnZoneApprovals';
import OnlineCourseSales from './pages/AppAdmin/OnlineCourseSales';
import AdminControlScreens from './pages/AppAdmin/AdminControlScreens';
import MembershipPlansAdmin from './pages/AppAdmin/MembershipPlansAdmin';
import MembershipSubscribersAdmin from './pages/AppAdmin/MembershipSubscribersAdmin';
import NavigatorsAdmin from './pages/AppAdmin/NavigatorsAdmin';
import NavAppControls from './pages/AppAdmin/NavAppControls';
import BusinessPostsAdmin from './pages/AppAdmin/BusinessPostsAdmin';
import TownPulseAdmin from './pages/AppAdmin/TownPulseAdmin';
import BusinessAdsManage from './pages/UserDashboard/BusinessAdsManage';
import CitizenRequirementsInbox from './pages/FranchiseeDashboard/CitizenRequirementsInbox';
import AppOperatorLogin from './pages/AppAdmin/AppOperatorLogin';
import BusinessMyOperators from './pages/BusinessAdmin/BusinessMyOperators';
import BusinessAdminOperatorLogin from './pages/BusinessAdmin/BusinessAdminOperatorLogin';
import CardVisibilityGuard from './components/CardVisibilityGuard';
import { pathnameAllowedByFranchiseFeatures } from './config/franchiseeFeatures';

/** Permission key for Admin Control Screens hub paths (operators need grant). */
const acPerm = (path) => generateAdminControlPermissionName(path);

const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount/DeleteAccount'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy/RefundPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy/CookiePolicy'));
const SmartBillWithQR = lazy(() => import('./pages/Bills/SmartBillWithQR'));
const CartModelBill = lazy(() => import('./pages/Bills/CartModelBill'));
const JobAlertsManagement = lazy(() => import('./pages/JobAlerts/JobAlertsManagement'));
const SmartJobAlertCreation = lazy(() => import('./pages/JobAlerts/SmartJobAlertCreation'));
const StudentsManagement = lazy(() => import('./pages/Students/StudentsManagement'));
const CourseBuilderPage = lazy(() => import('./pages/Students/CourseBuilderPage'));
const RouteLoader = ({ children }) => <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '45vh' }}><CircularProgress /></Box>}>{children}</Suspense>;

// Lazy load PdfResumeDownload to prevent build failures if file is deleted
// If file doesn't exist, this will gracefully handle the error
const PdfResumeDownload = lazy(() =>
  import('./pages/UserDashboard/PdfResumeDownload').catch(() => {
    // Return a fallback component if file is deleted
    return {
      default: () => (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            This page is no longer available.
          </Typography>
          <Button onClick={() => window.history.back()} sx={{ mt: 2 }} variant="contained">
            Go Back
          </Button>
        </Box>
      )
    };
  })
);

// Route Validator Component - checks if route is valid before auth check
const RouteValidator = ({ children }) => {
  const location = useLocation();

  // Define ALL valid route patterns that exist in the application
  const validRoutePatterns = [
    '/dashboard',
    '/profile',
    '/users',
    '/wallet',
    '/bills',
    '/customer',
    '/coupons',
    '/shop-orders',
    '/job-alerts',
    '/jalertsmngmt',
    '/professions',
    '/shops',
    '/net-centers',
    '/online-works',
    '/settings',
    '/app-admin',
    '/app-admin-operator',
    '/business-admin-operator',
    '/backup',
    '/dashboard-cards',
    '/admin-control-screens',
    '/apptrends',
    '/catalog-templates',
    '/mitrascreens',
    '/citizenscreens',
    '/loksyhomescreen_controls',
    '/mytownscreens',
    '/testersfeedback',
    '/betagate',
    '/appnotifications',
    '/services',
    '/courses',
    '/training-programs',
    '/user',
    '/locsy',
    '/franchisee',
    '/business-admin',
    '/content',
    '/business-category-index',
    '/vyapar_freelisting',
    '/vyapar_failedotp',
    '/business-tick-settings',
    '/mitra-token-board-settings',
    '/citizen-posting-categories',
    '/user-postings',
    '/locsydashboard',
    '/tables',
    '/oldsite',
    '/disclaimer',
    '/terms-and-conditions',
    '/locsy-terms-and-conditions',
    '/privacy-policy',
    '/privacy',
    '/refund-policy',
    '/cookie-policy',
    '/delete-account',
    '/certificate',
    '/certificate-verification',
    '/certificateverification',
    '/certificateverfication',
    '/bills/admin-controls',
    '/bills/whatsapp-config',
    '/dashboard/students',
    '/dashboard/students/courses',
    '/student',
    '/application-chat',
    '/user/confirmations',
    '/url-manager',
    '/ld2hd',
    '/s',
    '/demo',
    '/book-demo',
    '/vyapar',
    '/loksymitra',
    '/loksyapp',
    '/loksy-mitra',
    '/loksy-app',
  ];

  // Check if the current path matches any valid route pattern
  const isValidRoute = validRoutePatterns.some(pattern => {
    // Check if pathname starts with any valid pattern
    return location.pathname === pattern || location.pathname.startsWith(pattern + '/');
  });

  // If route is not valid, show NotFound directly (no auth required)
  if (!isValidRoute) {
    return <NotFound />;
  }

  if (!isLocalInternalHost() && isLocalInternalPath(location.pathname)) {
    return <NotFound />;
  }

  // If route is valid, render children (which will go through ProtectedRoute)
  return children;
};

// /job-alerts: public job list for guests; staff keep admin Job Alerts management UI
const JOB_ALERTS_STAFF_ROLES = [
  'app_admin',
  'super_admin',
  'app_operator',
  'franchisee_admin',
  'net_center_admin',
  'business_admin',
];

const JobAlertsPathEntry = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated && JOB_ALERTS_STAFF_ROLES.includes(user?.role)) {
    return (
      <ProtectedRoute
        requiredRole={JOB_ALERTS_STAFF_ROLES}
        requiredPermission="job_alerts"
      >
        <DashboardLayout>
          <RouteLoader><JobAlertsManagement /></RouteLoader>
        </DashboardLayout>
      </ProtectedRoute>
    );
  }

  return <PublicJobAlerts />;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { user, isAuthenticated, hasRole, loading, getDefaultRoute } = useAuth();
  const location = useLocation();
  // Preserve user routes - if user is on /user/* routes, keep them there on refresh
  const isUserRoute = location.pathname.startsWith('/user/');
  const isFranchiseeRoute = location.pathname.startsWith('/franchisee/') || location.pathname.startsWith('/locsy/');

  // Define admin routes that normal users should NOT access
  const adminRoutes = [
    '/dashboard',
    '/bills/create',
    '/bills/cart-model',
    '/bills/view',
    '/bills/pending-works',
    '/bills/advance-bills',
    '/bills/enquiries',
    '/bills/enquiry-form',
    '/bills/drafts',
    '/bills/post-bill-coupons',
    '/bills/service-charges',
    '/bills/counter-sales-datewise',
    '/bills/services',
    '/bills/main-categories',
    '/bills/inventory',
    '/customer/search',
    '/customer/dashboard',
    '/users',
    '/wallet',
    '/coupons',
    '/shop-orders',
    '/job-alerts',
    '/job-alerts/smart-create',
    '/job-alerts/subscribers/manage',
    '/application-chat',
    '/job-alerts/view',
    '/job-alerts/approval',
    '/jalertsmngmt',
    '/professions',
    '/shops',
    '/net-centers',
    '/online-works',
    '/settings',
    '/app-admin/my-operators',
    '/business-category-index',
    '/mitra-token-board-settings',
    '/citizen-posting-categories',
    '/user-postings',
    '/backup'
  ];

  // Check if current route is an admin route
  const isAdminRoute = adminRoutes.some(route => location.pathname === route || location.pathname.startsWith(route + '/'));
  const isPublicRoute = location.pathname === '/services' ||
    location.pathname === '/courses' ||
    location.pathname === '/training-programs' ||
    location.pathname === '/enquiryform' ||
    location.pathname === '/enquiry-form' ||
    location.pathname === '/demo' ||
    location.pathname === '/book-demo' ||
    location.pathname === '/vyapar' ||
    location.pathname === '/loksymitra' ||
    location.pathname === '/loksyapp' ||
    location.pathname === '/jobalerts' ||
    location.pathname === '/job-alerts' ||
    location.pathname === '/certificate/verify' ||
    location.pathname === '/certificate-verification' ||
    location.pathname === '/certificateverification' ||
    location.pathname === '/certificateverfication' ||
    location.pathname === '/profile' ||
    location.pathname.startsWith('/profile/');

  // CRITICAL: Wait for auth to finish loading before checking authentication
  // This prevents redirect to login page during page refresh when token is being restored
  if (loading) {
    // Show loading spinner to prevent flash of login page during refresh
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if user is authenticated
  // Only redirect if not already on a login page to prevent redirect loops
  if (!isAuthenticated) {
    const currentPath = location.pathname;
    const isLoginPage = currentPath === '/aalogin' ||
      currentPath === '/user-login' ||
      currentPath === '/user-login/forgot-password' ||
      currentPath === '/locsydashboard' ||
      currentPath === '/locsydashboard/login' ||
      currentPath === '/locsydashboard/forgot-password' ||
      currentPath === '/locsydashboard/forgot-pin' ||
      currentPath === '/locsy/admin/login' ||
      currentPath === '/locsy/admin/forgot-password' ||
      currentPath === '/locsy/admin/forgot-pin' ||
      currentPath === '/locsy/operator/login' ||
      currentPath === '/franchisee/admin/login' ||
      currentPath === '/franchisee/operator/login' ||
      currentPath === '/app-admin-operator/login' ||
      currentPath === '/business-admin-operator/login';

    // Keep selected routes publicly accessible without login.
    if (isPublicRoute) {
      return children;
    }

    // Don't redirect if already on a login page
    if (!isLoginPage) {
      // Redirect user routes to user login; operators back to operator login.
      const redirectTo = isUserRoute ? '/user-login' : getRememberedLoginRoute();
      return <Navigate to={redirectTo} replace />;
    }
  }

  // CRITICAL SECURITY: Block normal users (user/customer) from accessing admin routes
  if (user && (user.role === 'user' || user.role === 'customer')) {
    // Normal users can ONLY access /user/* routes
    if (isAdminRoute && !isUserRoute) {
      // Redirect to customer coupons (auto-login share landing)
      return <Navigate to="/user/coupons" replace />;
    }
    // Allow access to user routes, /services, /courses, and /profile (public services and profile)
    if (isUserRoute || isPublicRoute) {
      return children;
    }
    // Block all other routes
    return <Navigate to="/user/coupons" replace />;
  }

  // Block franchisee users from accessing app admin routes (unless it's their own routes)
  if (
    user &&
    (user.role === 'franchisee_admin' ||
      user.role === 'net_center_admin' ||
      user.role === 'business_admin' ||
      user.role === 'franchisee_operator')
  ) {
    const allowedFranchiseeRoutesLegacy = [
      '/locsy/dashboard',
      '/locsy/products',
      '/locsy/shop-photos',
      '/locsy/ads',
      '/locsy/operators',
      '/business-admin/my-operators',
      '/franchisee/dashboard',
      '/franchisee/ads',
      '/franchisee/operators',
      '/bills/create',
      '/bills/cart-model',
      '/bills/view',
      '/bills/pending-works',
      '/bills/advance-bills',
      '/bills/enquiries',
      '/bills/enquiry-form',
      '/bills/drafts',
      '/bills/operator-calls',
      '/bills/post-bill-coupons',
      '/bills/services',
      '/bills/main-categories',
      '/bills/inventory',
      '/professions',
      '/coupons',
      '/shop-orders',
      '/dashboard/students',
      '/dashboard/training-programs',
      '/services',
      '/courses',
      '/training-programs',
      '/online-works',
      '/application-chat',
      '/backup',
      '/profile',
      '/bills/admin-controls',
      '/franchisee/operator/login',
      '/locsy/operator/login',
    ];

    const appAdminOnlyRoutes = [
      '/users',
      '/wallet',
      '/job-alerts',
      '/jalertsmngmt',
      '/application-chat',
      '/backup',
      '/tables',
      '/job-alerts/subscribers/manage',
      '/shops',
      '/net-centers',
      '/settings',
      '/app-admin/my-operators',
      '/locsy/admin/verification',
      '/locsy/admin/approval',
      '/locsy/admin/franchisee-saas-pricing',
      '/locsy/admin/razorpay-test',
      '/franchisee/admin/approval',
      '/franchisee/admin/verification',
      '/business-admin/approval',
      '/business-admin/verification',
      '/business-category-index',
      '/mitra-token-board-settings',
      '/citizen-posting-categories',
      '/user-postings',
    ];

    const isAppAdminOnlyRoute = appAdminOnlyRoutes.some(
      (route) =>
        location.pathname === route ||
        location.pathname.startsWith(route + '/'),
    );

    if (isAppAdminOnlyRoute) {
      return <Navigate to="/locsy/dashboard" replace />;
    }

    let isAllowedRoute;
    if (user.role === 'franchisee_admin') {
      isAllowedRoute = pathnameAllowedByFranchiseFeatures(
        location.pathname,
        user.features,
      );
    } else if (user.role === 'franchisee_operator') {
      const operatorAllowedRoutes = getFranchiseeOperatorAllowedPaths(
        user.permissions,
      );
      isAllowedRoute = operatorAllowedRoutes.some(
        (route) =>
          location.pathname === route ||
          location.pathname.startsWith(route + '/'),
      );
    } else {
      isAllowedRoute = allowedFranchiseeRoutesLegacy.some(
        (route) =>
          location.pathname === route ||
          location.pathname.startsWith(route + '/'),
      );
    }

    if (!isAllowedRoute && !isFranchiseeRoute) {
      return <Navigate to="/locsy/dashboard" replace />;
    }
  }

  // Franchisee operator access is handled by the permission-based route map above.
  // Skip generic role-gate checks here to avoid false redirects when requiredRole
  // doesn't explicitly include franchisee_operator.
  if (user?.role === 'franchisee_operator') {
    return children;
  }

  // Check role requirement - but preserve user routes
  if (requiredRole && !isUserRoute) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!hasRole(roles)) {
      // For operators, check if they have the required permission
      if (user?.role === 'app_operator' && requiredPermission) {
        const operatorEffective = expandLegacyOperatorPermissions(
          Array.isArray(user?.permissions) ? user.permissions : [],
        );
        const needed = String(requiredPermission).trim().toLowerCase();
        if (!operatorEffective.includes(needed)) {
          return <Navigate to={getDefaultRoute(user?.role)} replace />;
        }
      } else if (!hasRole(roles)) {
        return <Navigate to={getDefaultRoute(user?.role)} replace />;
      }
    }
  }

  // Check permission requirement (for operators) - but preserve user routes
  if (requiredPermission && user?.role === 'app_operator' && !isUserRoute) {
    const operatorEffective = expandLegacyOperatorPermissions(
      Array.isArray(user?.permissions) ? user.permissions : [],
    );
    const needed = String(requiredPermission).trim().toLowerCase();
    if (!operatorEffective.includes(needed)) {
      return <Navigate to={getDefaultRoute(user?.role)} replace />;
    }
  }

  return children;
};

function App() {
  const { isAuthenticated, user, getDefaultRoute, loading } = useAuth();

  return (
    <>
      <Routes>
        {/* Public Routes - Must be defined BEFORE root route to prevent redirects */}

        {/* LOCSY Admin Registration - Public Route (Always accessible) */}
        <Route
          path="/locsy/admin/register"
          element={<FranchiseeAdminRegister />}
        />

        {/* Legacy alias - redirect to LOCSY route */}
        <Route
          path="/franchisee/admin/register"
          element={<Navigate to="/locsy/admin/register" replace />}
        />

        {/* Business/Service Admin Registration alias */}
        <Route
          path="/locsydashboard/register"
          element={<FranchiseeAdminRegister />}
        />

        {/* LOCSY Admin Login - Public Route (Always accessible, even if logged in) */}
        <Route
          path="/locsy/admin/login"
          element={<FranchiseeAdminLogin />}
        />

        {/* Legacy alias - redirect to LOCSY route */}
        <Route
          path="/franchisee/admin/login"
          element={<Navigate to="/locsy/admin/login" replace />}
        />

        {/* LOCSY Business Dashboard Login aliases */}
        <Route
          path="/locsydashboard"
          element={<FranchiseeAdminLogin />}
        />
        <Route
          path="/locsydashboard/login"
          element={<FranchiseeAdminLogin />}
        />

        {/* LOCSY Operator Login - Public Route (Always accessible, even if logged in) */}
        <Route
          path="/locsy/operator/login"
          element={<FranchiseeOperatorLogin />}
        />

        {/* Legacy alias - redirect to LOCSY route */}
        <Route
          path="/franchisee/operator/login"
          element={<Navigate to="/locsy/operator/login" replace />}
        />

        {/* App Admin Operator Login - Public Route (Always accessible, even if logged in) */}
        <Route
          path="/app-admin-operator/login"
          element={<AppOperatorLogin />}
        />

        {/* Business Admin Operator Login - Public Route */}
        <Route
          path="/business-admin-operator/login"
          element={<BusinessAdminOperatorLogin />}
        />
        <Route
          path="/business-admin-operator"
          element={<Navigate to="/business-admin-operator/login" replace />}
        />

        {/* LOCSY Admin Forgot Password - Public Route */}
        <Route
          path="/locsy/admin/forgot-password"
          element={<ForgotPassword />}
        />

        {/* Legacy alias - redirect to LOCSY route */}
        <Route
          path="/franchisee/admin/forgot-password"
          element={<Navigate to="/locsy/admin/forgot-password" replace />}
        />

        {/* Business/Service Admin Forgot Password alias */}
        <Route
          path="/locsydashboard/forgot-password"
          element={<ForgotPassword />}
        />

        {/* Mitra reset PIN via mobile OTP */}
        <Route
          path="/locsydashboard/forgot-pin"
          element={<ForgotPin />}
        />
        <Route
          path="/locsy/admin/forgot-pin"
          element={<ForgotPin />}
        />

        {/* Citizen / user portal forgot password */}
        <Route
          path="/user-login/forgot-password"
          element={<ForgotPassword variant="user" />}
        />

        {/* Magic login route for customers */}
        <Route path="/magic-login" element={<MagicLogin />} />

        {/* Public Job Alerts — /jobalerts and /job-alerts (staff on /job-alerts still get admin UI) */}
        <Route path="/jobalerts" element={<PublicJobAlerts />} />
        <Route path="/job-alerts" element={<JobAlertsPathEntry />} />

        {/* Public enquiry form — OTP verified, no login (services.knls.in/enquiryform) */}
        <Route path="/enquiryform" element={<PublicEnquiryForm />} />
        <Route path="/enquiry-form" element={<PublicEnquiryForm />} />

        {/* Shop QR scan: Lens opens this responsive catalog without requiring the app. */}
        <Route path="/s/:shopId" element={<PublicShopCatalog />} />
        <Route path="/v/:shopId/products" element={<PublicShopCatalog />} />
        <Route path="/t/:shopId/:indentId" element={<PublicTokenBoard />} />
        <Route path="/citizen/home" element={<CitizenHomeComplete />} />
        <Route path="/citizen/mytown" element={<CitizenMyTown />} />

        {/* Redirect /students to student login page (for student access) */}
        <Route path="/students" element={<Navigate to="/student/login" replace />} />

        <Route path="/terms-and-conditions" element={<TermsPage />} />
        <Route path="/locsy-terms-and-conditions" element={<LocsyTermsPage />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route
          path="/refund-policy"
          element={
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>}>
              <RefundPolicy />
            </Suspense>
          }
        />
        <Route
          path="/cookie-policy"
          element={
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>}>
              <CookiePolicy />
            </Suspense>
          }
        />

        {/* Public Privacy Policy & Account Deletion — Locsy Citizen / Play Store */}
        <Route
          path="/privacy-policy"
          element={
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>}>
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route
          path="/delete-account"
          element={
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>}>
              <DeleteAccount />
            </Suspense>
          }
        />

        {/* Public Student Verification Page - No authentication required */}
        <Route path="/students/verify" element={<StudentVerification />} />

        {/* Public Certificate Verification Page */}
        <Route path="/certificate-verification" element={<CertificateVerification />} />
        <Route path="/certificateverification" element={<CertificateVerification />} />
        <Route path="/certificateverfication" element={<CertificateVerification />} />

        {/* Legacy certificate verification route */}
        <Route path="/certificate/verify" element={<CertificateVerification />} />

        {/* Student Login Page - Public */}
        <Route path="/student/login" element={<StudentLogin />} />

        {/* Student Routes - Login is default entry */}
        <Route path="/student/dashboard" element={<StudentDashboardPage />} />
        <Route path="/student/referrals" element={<StudentReferralsPage />} />
        <Route path="/student/live-certificate" element={<StudentLiveCertificatePage />} />
        <Route path="/student/track-sheet" element={<DailyTrackSheet />} />
        <Route path="/student/track-sheet/history" element={<TrackSheetHistory />} />
        <Route path="/student/statistics" element={<StudentStatistics />} />
        {/* Student LMS Routes */}
        <Route path="/student/my-courses" element={<MyCourses />} />
        <Route path="/student/courses/:courseId/practice-ide" element={<PracticeIdeScreen />} />
        <Route path="/student/courses/:courseId/coding/:contentId" element={<CodingChallengeScreen />} />
        <Route path="/student/courses/:courseId" element={<StudentCourseViewer />} />
        <Route path="/student/typing-tutor" element={<TypingTutorHome />} />
        <Route path="/student/typing-tutor/games/:gameId/:variantId" element={<TypingTutorGamePlay />} />
        <Route path="/student/typing-tutor/games" element={<TypingTutorGamesHub />} />
        <Route path="/student/leaderboard" element={<LmsLeaderboardPage />} />
        <Route path="/student/typing-tutor/:exerciseType" element={<TypingTutorExerciseList />} />
        <Route path="/student/typing-tutor/:exerciseType/:lessonId" element={<TypingTutorLesson />} />
        {/* Default redirect: /student -> login */}
        <Route path="/student" element={<Navigate to="/student/login" replace />} />

        {/* Block old /login route - show 404 */}
        <Route
          path="/login"
          element={<NotFound />}
        />

        {/* Auth Routes - Admin Login */}
        <Route
          path="/aalogin"
          element={
            loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress />
              </Box>
            ) : isAuthenticated ? (
              <Navigate to={getDefaultRoute(user?.role)} replace />
            ) : (
              <AuthLayout>
                <Login />
              </AuthLayout>
            )
          }
        />

        {/* Root path — cinematic landing for guests; signed-in users go to their hub */}
        <Route
          path="/"
          element={
            loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress />
              </Box>
            ) : isAuthenticated ? (
              <Navigate to={getDefaultRoute(user?.role)} replace />
            ) : (
              <LoksyLanding />
            )
          }
        />

        <Route path="/portals" element={<PortalHome />} />
        <Route path="/demo" element={<LoksyDemo />} />
        <Route path="/book-demo" element={<Navigate to="/demo" replace />} />
        <Route path="/vyapar" element={<Navigate to="/demo" replace />} />
        <Route path="/loksymitra" element={<LoksyMitraStory />} />
        <Route path="/loksy-mitra" element={<Navigate to="/loksymitra" replace />} />
        <Route path="/loksyapp" element={<LoksyAppStory />} />
        <Route path="/loksy-app" element={<Navigate to="/loksyapp" replace />} />

        {/* User Login Route - Always show login page, let component handle redirects */}
        <Route
          path="/user-login"
          element={
            loading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '100vh',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <UserLogin />
            )
          }
        />

        {/* User Routes - With UserLayout (navbar and profile menu) */}
        <Route
          path="/user/*"
          element={
            <RouteValidator>
              <ProtectedRoute>
                <UserLayout>
                  <Routes>
                    <Route
                      path="dashboard"
                      element={<UserDashboard />}
                    />
                    <Route
                      path="job-alerts"
                      element={
                        <CardVisibilityGuard>
                          <UserJobAlertsView />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="job-alerts-registration"
                      element={<JobAlertsRegistration />}
                    />
                    <Route
                      path="coupons"
                      element={
                        <CardVisibilityGuard>
                          <UserCouponsView />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <CardVisibilityGuard>
                          <UserShopOrders />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="redeem"
                      element={
                        <CardVisibilityGuard>
                          <RedeemCouponOrder />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="ads"
                      element={<UserAdsView />}
                    />
                    <Route
                      path="career-profile"
                      element={
                        <CardVisibilityGuard>
                          <CareerProfileForm />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="resume"
                      element={
                        <CardVisibilityGuard>
                          <ResumeGeneration />
                        </CardVisibilityGuard>
                      }
                    />
                    <Route
                      path="profile"
                      element={<Profile />}
                    />
                    <Route
                      path="confirmations"
                      element={<ApplicationChat />}
                    />
                    {/* PdfResumeDownload route - commented out/hidden, can be safely deleted */}
                    {/* <Route
                    path="pdfresume"
                    element={
                      <CardVisibilityGuard>
                        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}>
                          <PdfResumeDownload />
                        </Suspense>
                      </CardVisibilityGuard>
                    }
                  /> */}
                  </Routes>
                </UserLayout>
              </ProtectedRoute>
            </RouteValidator>
          }
        />

        {/* Protected Dashboard Routes - Admin/Franchisee routes with DashboardLayout */}
        <Route
          path="/*"
          element={
            <RouteValidator>
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    {/* Dashboard - Only for Admin/Operator, NOT for normal users */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator']}>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/application-chat"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator', 'net_center_admin', 'shop_admin', 'franchisee_admin']}>
                          <ApplicationChat />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile/sessions"
                      element={
                        <ProtectedRoute>
                          <SessionManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* User Management */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/users/analytics"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <UserAnalytics />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/users/:id"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <UserDetails />
                        </ProtectedRoute>
                      }
                    />

                    {/* Wallet Management — dummy points/boost ledger replaced by Coin Economy */}
                    <Route
                      path="/wallet"
                      element={
                        <Navigate to="/content/coin-management" replace />
                      }
                    />
                    <Route
                      path="/wallet/transactions"
                      element={
                        <Navigate to="/content/coin-management" replace />
                      }
                    />

                    {/* CRITICAL: Smart Bill (QR) - Only for Admin/Operator/Franchisee - NOT for normal users */}
                    <Route
                      path="/bills/create"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin', 'business_admin']}
                          requiredPermission="bills_creation"
                        >
                          <RouteLoader><SmartBillWithQR /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/cart-model"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin', 'business_admin']}
                          requiredPermission="bills_creation"
                        >
                          <RouteLoader><CartModelBill /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/view"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin', 'business_admin']}
                          requiredPermission="bills_view"
                        >
                          <ViewBills />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/manage"
                      element={
                        isLocalInternalHost() ? (
                          <ProtectedRoute
                            requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                            requiredPermission="old_site_bills"
                          >
                            <OldSiteBills />
                          </ProtectedRoute>
                        ) : (
                          <NotFound />
                        )
                      }
                    />
                    <Route
                      path="/bills/pending-works"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}>
                          <PendingWorks />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/advance-bills"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin', 'business_admin']}
                          requiredPermission="bills_view"
                        >
                          <ViewBills advanceOnly />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/enquiries"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                          requiredPermission="enquiries"
                        >
                          <Enquiries />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/enquiry-form"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                          requiredPermission="enquiries"
                        >
                          <EnquiryForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/drafts"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin', 'business_admin']}
                          requiredPermission="bills_creation"
                        >
                          <SmartBillDrafts />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/post-bill-coupons"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}>
                          <PostBillCoupons />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/operator-calls"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                          requiredPermission="operator_call_leads"
                        >
                          <LeadsManagementTabs />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer/search"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                          requiredPermission="customer_search"
                        >
                          <CustomerSearch />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer/dashboard"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}
                          requiredPermission="customer_dashboard"
                        >
                          <CustomerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/service-charges"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ServiceChargesChallans />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/counter-sales-datewise"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <CounterSalesDatewise />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/services"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ServicesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/main-categories"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <MainCategoriesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/inventory"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <InventoryHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/admin-controls"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin', 'app_operator']}
                          requiredPermission={acPerm('/bills/admin-controls')}
                        >
                          <AdminControls />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bills/whatsapp-config"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'franchisee_operator', 'app_operator']}
                          requiredPermission={acPerm('/bills/whatsapp-config')}
                        >
                          <WhatsAppMessageConfig />
                        </ProtectedRoute>
                      }
                    />
                    {/* Services - Accessible to all authenticated users (including normal users) */}
                    <Route
                      path="/services"
                      element={
                        <ProtectedRoute>
                          <CardVisibilityGuard>
                            <KNLSServices />
                          </CardVisibilityGuard>
                        </ProtectedRoute>
                      }
                    />
                    {/* Courses - Accessible to all authenticated users (including normal users) */}
                    <Route
                      path="/courses"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/training-programs" replace />
                        </ProtectedRoute>
                      }
                    />
                    {/* Training Programs - Public view page backed by database */}
                    <Route
                      path="/training-programs"
                      element={
                        <ProtectedRoute>
                          <TrainingPrograms />
                        </ProtectedRoute>
                      }
                    />
                    {/* Disclaimer - Accessible to all authenticated users */}
                    <Route
                      path="/disclaimer"
                      element={
                        <ProtectedRoute>
                          <Disclaimer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/professions"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ProfessionsManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Coupons Management — tabs: CRUD / Issued / Used */}
                    <Route
                      path="/coupons"
                      element={
                        <ProtectedRoute
                          requiredRole={[
                            'app_admin',
                            'super_admin',
                            'franchisee_admin',
                            'net_center_admin',
                            'franchisee_operator',
                            'business_admin',
                            'shop_admin',
                          ]}
                        >
                          <CouponsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/coupons/issued"
                      element={<Navigate to="/coupons?tab=issued" replace />}
                    />
                    {/* Citizen redeem orders inbox (same shop-indents as Flutter Mitra) */}
                    <Route
                      path="/shop-orders"
                      element={
                        <ProtectedRoute
                          requiredRole={[
                            'app_admin',
                            'super_admin',
                            'franchisee_admin',
                            'net_center_admin',
                            'franchisee_operator',
                            'app_operator',
                            'business_admin',
                            'shop_admin',
                          ]}
                        >
                          <ShopOrdersInbox />
                        </ProtectedRoute>
                      }
                    />

                    {/* Job Alerts Management */}
                    <Route
                      path="/job-alerts"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']} requiredPermission="job_alerts">
                          <RouteLoader><JobAlertsManagement /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/jalertsmngmt"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <JAlertsMngmt />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/job-alerts/smart-create"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission="job_alerts_smart_create"
                        >
                          <RouteLoader><SmartJobAlertCreation /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin Subscribers Management - Auth Required */}
                    <Route
                      path="/job-alerts/subscribers/manage"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <SubscribersManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/job-alerts/view"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator', 'franchisee_admin', 'net_center_admin']}>
                          <JobAlertsView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/job-alerts/approval"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/job-alerts/approval')}
                        >
                          <JobAlertsApproval />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/job-alerts/enquiries"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <JobAlertsEnquiries />
                        </ProtectedRoute>
                      }
                    />

                    {/* Students Management - More specific routes first */}
                    <Route
                      path="/dashboard/students/dashboard/:phone"
                      element={
                        <ProtectedRoute>
                          <StudentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/register"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <StudentRegistration />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/registerwithbill"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <StudentRegistrationWithBill />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/courses"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin', 'app_operator']}
                          requiredPermission="lms_builder"
                        >
                          <CourseManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/course-referrals"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <LearnZonePeerReferrals />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/training-programs"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <TrainingProgramsManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/courses/:courseId/builder"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}
                          requiredPermission="lms_builder"
                        >
                          <RouteLoader><CourseBuilderPage /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/courses/:courseId/display-settings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin', 'app_operator']}
                          requiredPermission="lms_builder"
                        >
                          <CourseDisplaySettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/flashcards"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}>
                          <FlashcardsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/reports"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <StudentReportsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/assignments/review"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}>
                          <AssignmentReviewPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/quizzes/review"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}>
                          <QuizReviewPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/coding/review"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}>
                          <CodingReviewPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/coding/problems"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'app_operator', 'franchisee_admin']}>
                          <CodingProblemsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <RouteLoader><StudentsManagement /></RouteLoader>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/track-sheets"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <AdminTrackSheetsDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/track-sheets/old"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin']}>
                          <StudentTrackSheetsAdmin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/students/attendance"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin', 'franchisee_admin', 'app_operator']} requiredPermission="digital_attendance">
                          <AdminAttendance />
                        </ProtectedRoute>
                      }
                    />

                    {/* User Dashboard Routes - Moved to separate route group above (outside DashboardLayout) */}

                    {/* LOCSY Dashboard Routes */}
                    <Route
                      path="/locsy/dashboard"
                      element={
                        <ProtectedRoute>
                          <FranchiseeDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/accounts"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <AccountsHub />
                        </ProtectedRoute>
                      }
                    />                    <Route
                      path="/locsy/products"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ProductsHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/products/groups"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <MainCategoriesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/products/catalog"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ServicesManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/products/inventory"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <InventoryHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/products/taxes"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <BillTaxesSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/shop-photos"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <ShopPhotosUpdate />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/ads"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <BusinessAdsManage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Legacy aliases - redirect to LOCSY routes */}
                    <Route
                      path="/franchisee/dashboard"
                      element={<Navigate to="/locsy/dashboard" replace />}
                    />
                    <Route
                      path="/franchisee/ads"
                      element={<Navigate to="/locsy/ads" replace />}
                    />

                    {/* LOCSY Admin Verification - App Admin Only (approval concept removed) */}
                    <Route
                      path="/locsy/admin/verification"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission="business_admin_approval"
                        >
                          <FranchiseeAdminApproval />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/locsy/admin/approval"
                      element={<Navigate to="/locsy/admin/verification" replace />}
                    />

                    <Route
                      path="/locsy/admin/franchisee-saas-pricing"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/locsy/admin/franchisee-saas-pricing')}
                        >
                          <FranchiseeSaasPricingAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/locsy/admin/razorpay-test"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <RazorpayCheckoutDemo />
                        </ProtectedRoute>
                      }
                    />

                    {/* Legacy alias - redirect to LOCSY route */}
                    <Route
                      path="/franchisee/admin/saas-pricing"
                      element={<Navigate to="/locsy/admin/franchisee-saas-pricing" replace />}
                    />

                    {/* Legacy aliases → verification */}
                    <Route
                      path="/franchisee/admin/approval"
                      element={<Navigate to="/locsy/admin/verification" replace />}
                    />
                    <Route
                      path="/franchisee/admin/verification"
                      element={<Navigate to="/locsy/admin/verification" replace />}
                    />

                    <Route path="/business-admin/register" element={
                      <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator']} requiredPermission="vyapar_assisted_registration">
                        <AssistedRegistrationPage />
                      </ProtectedRoute>
                    } />
                    <Route
                      path="/business-admin/verification"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission="business_admin_approval"
                        >
                          <FranchiseeAdminApproval />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/business-admin/approval"
                      element={<Navigate to="/business-admin/verification" replace />}
                    />

                    <Route
                      path="/business-category-index"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/business-category-index')}
                        >
                          <BusinessCategoryIndex />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/vyapar_freelisting" element={<ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator']}><MyTownBusinessListings /></ProtectedRoute>} />
                    <Route path="/vyapar_failedotp" element={<ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator']}><VyaparFailedOtp /></ProtectedRoute>} />
                    <Route path="/business-tick-settings" element={<ProtectedRoute requiredRole={['app_admin', 'super_admin']}><BusinessTickSettings /></ProtectedRoute>} />

                    <Route
                      path="/mitra-token-board-settings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/mitra-token-board-settings')}
                        >
                          <MitraTokenBoardSettings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/citizen-posting-categories"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/citizen-posting-categories')}
                        >
                          <CitizenPostingCategories />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/user-postings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/user-postings')}
                        >
                          <UserPostings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/ad-settings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/ad-settings')}
                        >
                          <AdvertisementSettings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/career-resume-settings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/career-resume-settings')}
                        >
                          <CareerResumeSettings />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/learn-zone-approvals"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/learn-zone-approvals')}
                        >
                          <LearnZoneApprovals />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/online-course-sales"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/online-course-sales')}
                        >
                          <OnlineCourseSales />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin-control-screens"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'app_operator']}>
                          <AdminControlScreens />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/business-posts"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/business-posts')}
                        >
                          <BusinessPostsAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/townpulsescreen"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/townpulsescreen')}
                        >
                          <TownPulseAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/coupon-flow"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/coupon-flow')}
                        >
                          <CouponFlow />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/coupon-management"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/coupon-management')}
                        >
                          <CouponManagementAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/boost-control"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/boost-control')}
                        >
                          <BoostControl />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/coin-management"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/coin-management')}
                        >
                          <CoinManagement />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/razorpay"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/razorpay')}
                        >
                          <RazorpayLedger />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/membership-plans"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/membership-plans')}
                        >
                          <MembershipPlansAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/membership-subscribers"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/membership-plans')}
                        >
                          <MembershipSubscribersAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/navigators"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/navigators')}
                        >
                          <NavigatorsAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/nav-app-controls"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/nav-app-controls')}
                        >
                          <NavAppControls />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/apptrends"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/apptrends')}
                        >
                          <AppTrends />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/shop-polls"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/shop-polls')}
                        >
                          <ShopPollsAdmin />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/content/lucky-wheel"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/content/lucky-wheel')}
                        >
                          <LuckyWheel />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/franchisee/citizen-requirements"
                      element={
                        <ProtectedRoute>
                          <CitizenRequirementsInbox />
                        </ProtectedRoute>
                      }
                    />

                    {/* Business Dashboard protected alias */}
                    <Route
                      path="/locsydashboard/dashboard"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/locsy/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />

                    {/* Operator Management - LOCSY Admin Only */}
                    <Route
                      path="/locsy/operators"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <OperatorManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/franchisee/operators"
                      element={<Navigate to="/locsy/operators" replace />}
                    />

                    {/* Shops Management */}
                    <Route
                      path="/shops"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'net_center_admin']}>
                          <ShopsManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Net Centers Management */}
                    <Route
                      path="/net-centers"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <NetCentersManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Online Works Management */}
                    <Route
                      path="/online-works"
                      element={
                        <ProtectedRoute requiredRole={['net_center_admin', 'franchisee_admin']}>
                          <OnlineWorksManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* Settings */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/settings')}
                        >
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard-cards"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/dashboard-cards')}
                        >
                          <DashboardCards />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/catalog-templates"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/catalog-templates')}
                        >
                          <CatalogTemplates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/mitrascreens"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/mitrascreens')}
                        >
                          <MitraScreens />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/citizenscreens"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/citizenscreens')}
                        >
                          <CitizenScreens />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/loksyhomescreen_controls"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission="citizenscreens"
                        >
                          <CitizenScreens />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/mytownscreens"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/mytownscreens')}
                        >
                          <MyTownScreens />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/testersfeedback"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <TestersFeedback />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/betagate"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <BetaGate />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tables"
                      element={
                        isLocalInternalHost() ? (
                          <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                            <MyTables />
                          </ProtectedRoute>
                        ) : (
                          <NotFound />
                        )
                      }
                    />
                    <Route
                      path="/oldsite"
                      element={
                        isLocalInternalHost() ? (
                          <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                            <OldSite />
                          </ProtectedRoute>
                        ) : (
                          <NotFound />
                        )
                      }
                    />

                    {/* App Notifications — push to Students LMS, Mitra, Citizen */}
                    <Route
                      path="/appnotifications"
                      element={
                        <ProtectedRoute
                          requiredRole={['app_admin', 'super_admin', 'app_operator']}
                          requiredPermission={acPerm('/appnotifications')}
                        >
                          <AppNotifications />
                        </ProtectedRoute>
                      }
                    />

                    {/* URL Manager - For admin to manage and search URLs */}
                    <Route
                      path="/url-manager"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin']}>
                          <URLManager />
                        </ProtectedRoute>
                      }
                    />

                    {/* LD2HD - Free image upscale (Phase 1) */}
                    <Route
                      path="/ld2hd"
                      element={
                        <ProtectedRoute
                          requiredRole={[
                            'app_admin',
                            'super_admin',
                            'app_operator',
                            'franchisee_admin',
                            'net_center_admin',
                          ]}
                        >
                          <LD2HDUpscaler />
                        </ProtectedRoute>
                      }
                    />

                    {/* App Admin - My Operators */}
                    <Route
                      path="/app-admin/my-operators"
                      element={
                        <ProtectedRoute requiredRole={['app_admin']}>
                          <MyOperator />
                        </ProtectedRoute>
                      }
                    />

                    {/* Business Admin - My Operators (full feature parity; legacy /locsy/operators kept for testing) */}
                    <Route
                      path="/business-admin/my-operators"
                      element={
                        <ProtectedRoute requiredRole={['franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <BusinessMyOperators />
                        </ProtectedRoute>
                      }
                    />

                    {/* Fallback when catch-all /* matches (e.g. older deploys / SPA refresh) */}
                    <Route
                      path="/business-admin-operator/login"
                      element={<BusinessAdminOperatorLogin />}
                    />
                    <Route
                      path="/business-admin-operator"
                      element={<Navigate to="/business-admin-operator/login" replace />}
                    />

                    {/* Backup & Export */}
                    <Route
                      path="/backup"
                      element={
                        <ProtectedRoute requiredRole={['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin', 'shop_admin']}>
                          <Backup />
                        </ProtectedRoute>
                      }
                    />

                    {/* Default 404 for unknown routes */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            </RouteValidator>
          }
        />
      </Routes>
    </>
  );
}

export default App;

